import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { AnyObject, FORM_ERROR } from 'final-form';
import { AnyType } from '../interfaces';
import { useSelector } from 'react-redux';
import { AppRootState } from '../redux';
import { API_URL, UploadDocumentModel, useUploadDocument } from '../api';
import { useServiceRequestWithDocumentsInfoQuery } from '../graphql';
import { getAcceptFiles } from './HcpUpload';

export interface DocumentUploadProps {
  buttonAlign?: 'left' | 'right';
  serviceRequest?: AnyObject;
  buttonName?: string;
  caseDetail?: AnyObject;
}

export const DocumentUpload = ({ buttonAlign, serviceRequest, buttonName, caseDetail }: DocumentUploadProps) => {
  const [fileUploaded, setFileUploaded] = useState<File | null>(null);
  const hiddenFileInput = useRef<HTMLInputElement | null>(null);
  const programId = useSelector((state: AppRootState) => state.app.config?.programId);
  const organizationId = String(useSelector((state: AppRootState) => state.auth.session?.user?.OrganizationId ?? ''));
  const srTypes = useSelector(
    (state: AppRootState) => ((state.app?.entities?.features as AnyType) ?? {})['patient.intake.sr.creation'] as AnyType
  );
  const { mutate: uploadDocument } = useUploadDocument({ base: API_URL });
  const [uploadError, setUploadError] = useState<string | null>(null);
  const queryVars = {
    serviceRequestId: Number(serviceRequest?.[0]?.id ?? 0),
    organizationId: String(organizationId),
    programId: String(programId)
  };

  const resultSR = useServiceRequestWithDocumentsInfoQuery({ variables: queryVars });
  const [refreshServiceDoc, setRefreshServiceDoc] = useState(false);
  useEffect(() => {
    if (!refreshServiceDoc) return;
    if (resultSR && refreshServiceDoc && !caseDetail?.caseRecordId) {
      resultSR.refetch(queryVars);
      setRefreshServiceDoc(false);
    }
  }, [refreshServiceDoc]);

  const handleClick = (event: any) => {
    hiddenFileInput?.current?.click();
  };

  const saveFileSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    setFileUploaded(null);
    setUploadError(null);

    const target = e.target as HTMLInputElement;
    const files = target.files;

    if (!files) return;

    if (files.length > 3) {
      setUploadError(`You've exceeded the maximum number of files. Please upload 3 documents or less.`);
      target.value = '';
      return;
    }

    const totalSize = Array.from(files).reduce((acc, file) => acc + file.size, 0);
    const maxSize = 10 * 1024 * 1024; // 10 MB

    if (totalSize > maxSize) {
      const sizeInMB = (totalSize / (1024 * 1024)).toFixed(2);
      setUploadError(
        `The maximum combined file-size is 10 MB. The combined size of the uploaded files is ${sizeInMB} MB.`
      );
      target.value = '';
      return;
    }

    let errorMessages = '';
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!getDataType(file, 'application/pdf')) {
        errorMessages += 'Please upload a supported file-type (.pdf).\n';
        continue;
      }
      if (!getFileLength(file)) {
        errorMessages += `The maximum combined file-size is 10 MB. The combined size of the uploaded files is ${file?.size} MB.\n`;
        continue;
      }
      setFileUploaded(file); // show last valid file in UI
      await importFile(file);
    }

    if (errorMessages) {
      setUploadError(errorMessages.trim());
    }

    target.value = '';
  };

  const importFile = async (file: File) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async function () {
      const fileResult = reader.result ?? '';
      if (getDataType(file, 'application/pdf') && getFileLength(file)) {
        try {
          const payload: UploadDocumentModel = {
            serviceRequestId: caseDetail?.caseRecordId
              ? Number(caseDetail?.caseTransactionId)
              : Number(serviceRequest?.[0]?.id ?? 0),
            entityName: caseDetail?.caseRecordId
              ? caseDetail?.caseTransactionType.replace(/([A-Z])/g, ' $1').trim()
              : srTypes?.data?.SrEntityName,
            documentSource: srTypes?.data?.DocumentSource,
            createdBy: srTypes?.data?.SrCreatedBy,
            documentType: null,
            lookupDataListFunctionName: null,
            document: {
              fileData: fileResult.toString().replace('data:application/pdf;base64,', '') ?? '',
              fileName: file.name ?? ''
            }
          };
          const result = await uploadDocument(payload);
          if (result) {
            setRefreshServiceDoc(true);
          }
          if (result.errors && result.errors.length > 0) {
            setRefreshServiceDoc(false);
            return { [FORM_ERROR]: result.errors.map((e) => e.description).join(' ') };
          }
        } catch (ex: any) {
          setUploadError(ex.toString());
        }
      }
      return reader.result ?? '';
    };
    reader.onerror = function (error) {
      setUploadError(error.toString());
      return null;
    };
  };

  const errors = (uploadError || fileUploaded) && (
    <ul className='text-danger fs-2'>
      {uploadError &&
        typeof uploadError === 'string' &&
        uploadError.split('\n').map((msg, idx) => <li key={idx}>{msg}</li>)}
      {fileUploaded && !getDataType(fileUploaded, 'application/pdf') && (
        <li>Please upload a supported file-type (.pdf)</li>
      )}
      {fileUploaded && !getFileLength(fileUploaded) && <li>The maximum file size is 10 MB. Please try again.</li>}
      {/* {uploadError && <li>An error occurred while uploading the Document</li>} */}
      {fileUploaded && (!getDataType(fileUploaded, 'application/pdf') || !getFileLength(fileUploaded)) && (
        <p>Only {getAcceptFiles('.pdf')?.toUpperCase()} documents under 10 MB in size are supported</p>
      )}
    </ul>
  );

  const success = fileUploaded &&
    getDataType(fileUploaded, 'application/pdf') &&
    getFileLength(fileUploaded) &&
    !uploadError && (
      <ul className='text-success align-items-center' style={{ marginRight: '1em' }}>
        <p>Successfully Uploaded</p>
      </ul>
    );

  return (
    <>
      <div className={`d-flex align-items-left flex-row-reverse`} style={{ position: 'relative' }}>
        <div style={{ textAlign: 'center' }}>
          <Button variant='outline-danger px-3 py-1' className='mt-3 mt-md-0' onClick={handleClick}>
            {buttonName ?? ''}
          </Button>
          <input
            type='file'
            accept='.pdf'
            ref={hiddenFileInput}
            style={{ display: 'none' }}
            onChange={saveFileSelected}
            multiple
          />
        </div>
        <div>&nbsp;</div>
      </div>
      <div className={`d-flex align-items-left flex-row-reverse`} style={{ position: 'relative' }}>
        {success}
        {errors}
      </div>
    </>
  );
};

const getDataType = (file: File, fileType: string) => file?.type === fileType;

const getFileLength = (file: File) => file?.size <= 10e6;
